import React from 'react'
import { Show, TextField, DateField, ShowButton } from '@refinedev/antd'
import { useShow } from '@refinedev/core'
import { Typography, Tag, Divider } from 'antd'
import type { Tables } from '@muaa/shared'

// Embedded profile shape from the FK joins.
type RelProfile = Pick<Tables<'profiles'>, 'name' | 'title' | 'org' | 'class_year'>
type RelRequest = Pick<Tables<'mentorship_requests'>, 'id' | 'status'>
type Relationship = Tables<'mentorship_relationships'> & {
  mentor: RelProfile | null
  mentee: RelProfile | null
  request: RelRequest | null
}

const { Title, Text } = Typography

const STATUS_COLORS: Record<string, string> = {
  active: 'green',
  paused: 'orange',
  completed: 'blue',
  ended: 'red',
}

const KIND_LABEL: Record<string, string> = {
  one_on_one: 'One-on-one',
  circle: 'Circle',
}

// FK embeds: both parties + the originating request, resolved server-side.
const SELECT =
  '*, mentor:profiles!mentorship_relationships_mentor_profile_fkey(name,title,org,class_year), mentee:profiles!mentorship_relationships_mentee_profile_fkey(name,title,org,class_year), request:mentorship_requests!mentorship_relationships_request_id_fkey(id,status)'

/** One profile block: name + (title · org) + class year. */
function PartyBlock({ profile, fallbackId }: { profile: RelProfile | null; fallbackId: string }) {
  if (!profile) return <TextField value={fallbackId} />
  const sub = [profile.title, profile.org].filter(Boolean).join(' · ')
  return (
    <div>
      <TextField value={profile.name ?? fallbackId} />
      {sub ? (
        <div>
          <Text type="secondary">{sub}</Text>
        </div>
      ) : null}
      {profile.class_year ? (
        <div>
          <Text type="secondary">Class of {profile.class_year}</Text>
        </div>
      ) : null}
    </div>
  )
}

export function MentorshipRelationshipShow() {
  const { query } = useShow<Relationship>({ meta: { select: SELECT } })
  const isLoading = query.isLoading
  const record = query.data?.data

  return (
    <Show isLoading={isLoading}>
      <Title level={5}>Mentor</Title>
      <PartyBlock profile={record?.mentor ?? null} fallbackId={record?.mentor_id ?? '—'} />

      <Title level={5}>Mentee</Title>
      <PartyBlock profile={record?.mentee ?? null} fallbackId={record?.mentee_id ?? '—'} />

      <Divider />

      <Title level={5}>Status</Title>
      {record?.status ? (
        <Tag color={STATUS_COLORS[record.status] ?? 'default'}>{record.status}</Tag>
      ) : (
        <TextField value="—" />
      )}

      <Title level={5}>Kind</Title>
      <TextField value={record?.kind ? (KIND_LABEL[record.kind] ?? record.kind) : '—'} />

      <Title level={5}>Focus</Title>
      <TextField value={record?.focus ?? '—'} />

      <Title level={5}>Cadence</Title>
      <TextField value={record?.cadence ?? '—'} />

      <Title level={5}>Stream Channel ID</Title>
      <TextField value={record?.stream_channel_id ?? '—'} />

      <Divider />

      <Title level={5}>Started</Title>
      {record?.started_at ? (
        <DateField value={record.started_at} format="MMM D, YYYY h:mm A" />
      ) : (
        <TextField value="—" />
      )}

      <Title level={5}>Concluded</Title>
      {record?.concluded_at ? (
        <DateField value={record.concluded_at} format="MMM D, YYYY h:mm A" />
      ) : (
        <TextField value="—" />
      )}

      <Title level={5}>Completion Note</Title>
      <TextField value={record?.completion_note ?? '—'} />

      <Divider />

      <Title level={5}>Originating Request</Title>
      {record?.request_id ? (
        <ShowButton
          resource="mentorship_requests"
          recordItemId={record.request_id}
          size="small"
        >
          View request{record.request?.status ? ` (${record.request.status})` : ''}
        </ShowButton>
      ) : (
        <TextField value="No originating request" />
      )}
    </Show>
  )
}
